import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Shield,
  Phone,
  Mail,
  AlertTriangle,
  Search,
  CheckCircle,
  XCircle,
  Eye,
  Users,
  Globe,
  Zap,
  Star,
  Crown,
  Check,
  X,
  ArrowRight,
  ChevronRight,
  MessageSquare,
  UserX,
  DollarSign,
  Lock,
  Smartphone,
  FileWarning
} from "lucide-react";

const PRICING = {
  free: { monthly: 0, annual: 0 },
  basic: { monthly: 9.99, annual: 99.99 },
  advanced: { monthly: 24.99, annual: 249.99 },
  professional: { monthly: 49.99, annual: 499.99 }
};

const HomePage = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState("phone");
  const [searchValue, setSearchValue] = useState("");
  const [billingCycle, setBillingCycle] = useState("monthly");

  const handleQuickSearch = () => {
    if (searchValue.trim()) {
      navigate(`/register?search=${encodeURIComponent(searchValue)}&type=${searchType}`);
    } else {
      navigate("/register");
    }
  };

  const scamStats = [
    { label: "Lost to scams in 2024", value: "$12.5B", icon: DollarSign },
    { label: "Scam calls daily in US", value: "2B+", icon: Phone },
    { label: "Phishing emails sent", value: "3.4B/day", icon: Mail },
    { label: "Identity theft victims", value: "15M/year", icon: UserX },
  ];

  const testimonials = [
    { text: "Saved me from sending $500 to a fake 'IRS agent'. The phone lookup showed it was a VoIP scam number!", name: "Maria T.", location: "Texas" },
    { text: "Someone was catfishing my daughter. We traced their email and found out they were overseas.", name: "Robert K.", location: "Florida" },
    { text: "I use this for my small business. Caught 3 fake buyers trying to scam us last month alone.", name: "Jennifer L.", location: "California" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-7 h-7 text-red-500" />
            <span className="font-bold text-xl tracking-tight">
              <span className="text-red-500">Scam</span>Finder
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/login")}>
              Login
            </Button>
            <Button onClick={() => navigate("/register")} className="bg-red-500 hover:bg-red-600">
              Stop Scammers Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4 bg-gradient-to-b from-red-500/10 to-background">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <Badge className="bg-red-500/10 text-red-500 mb-4 text-sm px-4 py-1">
              <AlertTriangle className="w-4 h-4 mr-1" />
              Don't Be The Next Victim
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Got a Suspicious Call or Email?<br />
              <span className="text-red-500">Find Out If It's a Scam</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Instantly verify phone numbers, check emails, and unmask scammers before they steal your money or identity.
            </p>
          </div>

          {/* Quick Search Box */}
          <Card className="max-w-2xl mx-auto bg-card border-2 border-red-500/20 shadow-lg shadow-red-500/5">
            <CardContent className="p-6">
              <div className="flex gap-2 mb-4">
                <Button
                  variant={searchType === "phone" ? "default" : "outline"}
                  onClick={() => setSearchType("phone")}
                  className={searchType === "phone" ? "bg-red-500 hover:bg-red-600" : ""}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Phone
                </Button>
                <Button
                  variant={searchType === "email" ? "default" : "outline"}
                  onClick={() => setSearchType("email")}
                  className={searchType === "email" ? "bg-red-500 hover:bg-red-600" : ""}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>
              
              <div className="flex gap-3">
                <Input
                  type={searchType === "phone" ? "tel" : "email"}
                  placeholder={searchType === "phone" ? "Enter phone number (e.g., 555-123-4567)" : "Enter email address"}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="text-lg h-12"
                />
                <Button 
                  size="lg" 
                  onClick={handleQuickSearch}
                  className="bg-red-500 hover:bg-red-600 h-12 px-6"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Check Now
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground mt-3 text-center">
                Free account includes 2 lookups. No credit card required.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Scam Statistics */}
      <section className="py-12 px-4 bg-muted/30 border-y border-border">
        <div className="container mx-auto">
          <p className="text-center text-sm text-muted-foreground mb-6">THE SCAM EPIDEMIC IS REAL</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {scamStats.map((stat, i) => (
              <div key={i} className="text-center p-4">
                <stat.icon className="w-8 h-8 mx-auto mb-2 text-red-500" />
                <p className="text-2xl md:text-3xl font-bold">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Reveal */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">What We Reveal About Scammers</h2>
            <p className="text-muted-foreground">Every lookup exposes critical information to protect you</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Phone Lookup */}
            <Card className="bg-card/50 border-border hover:border-red-500/50 transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-red-500" />
                  </div>
                  Phone Number Lookup
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Carrier & Line Type</p>
                    <p className="text-sm text-muted-foreground">Is it a real phone or a burner/VoIP app?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Location Mapping</p>
                    <p className="text-sm text-muted-foreground">State and city based on area code</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">VoIP Detection</p>
                    <p className="text-sm text-muted-foreground">Flag Google Voice, TextNow, and burner apps</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Scam Risk Score</p>
                    <p className="text-sm text-muted-foreground">Instant assessment based on number patterns</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Lookup */}
            <Card className="bg-card/50 border-border hover:border-red-500/50 transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-red-500" />
                  </div>
                  Email Verification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Real or Fake?</p>
                    <p className="text-sm text-muted-foreground">Verify if the email actually exists</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Disposable Email Detection</p>
                    <p className="text-sm text-muted-foreground">Catch temporary/throwaway addresses</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Domain Reputation</p>
                    <p className="text-sm text-muted-foreground">Is the sender's domain trustworthy?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Fraud Risk Score</p>
                    <p className="text-sm text-muted-foreground">AI-powered scam probability rating</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Social Profile */}
            <Card className="bg-card/50 border-border hover:border-red-500/50 transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-500" />
                  </div>
                  Social Profile Search
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Find Linked Accounts</p>
                    <p className="text-sm text-muted-foreground">Discover social media tied to email/phone</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Catfish Detection</p>
                    <p className="text-sm text-muted-foreground">Verify if someone is who they claim to be</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Account Age Check</p>
                    <p className="text-sm text-muted-foreground">New accounts are often scammer red flags</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* IP Tracking */}
            <Card className="bg-card/50 border-border hover:border-red-500/50 transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Globe className="w-6 h-6 text-blue-500" />
                  </div>
                  IP & Location Tracking
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Geolocation Mapping</p>
                    <p className="text-sm text-muted-foreground">See where they're really located</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">VPN/Proxy Detection (95%)</p>
                    <p className="text-sm text-muted-foreground">Are they hiding their true location?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Tracking Links & Pixels</p>
                    <p className="text-sm text-muted-foreground">Create links that capture scammer IPs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Common Scam Types */}
      <section className="py-16 px-4 bg-red-500/5 border-y border-red-500/10">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-4">We Help You Fight These Scams</h2>
          <p className="text-center text-muted-foreground mb-10">Recognize and verify before you become a victim</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Phone, title: "IRS/Tax Scams", desc: "Fake agents demanding immediate payment" },
              { icon: MessageSquare, title: "Romance Scams", desc: "Catfish and fake online relationships" },
              { icon: DollarSign, title: "Payment Fraud", desc: "Fake buyers, sellers, and invoices" },
              { icon: Mail, title: "Phishing Emails", desc: "Fake banks, Amazon, PayPal messages" },
              { icon: Smartphone, title: "Tech Support Scams", desc: "Fake Microsoft/Apple warnings" },
              { icon: FileWarning, title: "Job Scams", desc: "Fake recruiters and work-from-home offers" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-background border border-border">
                <item.icon className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium">{item.title}</p>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-10">Real People, Real Protection</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="bg-card/50 border-border">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-amber-500 text-amber-500" />)}
                  </div>
                  <p className="text-sm mb-4">"{t.text}"</p>
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.location}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-emerald-500/10 text-emerald-500 mb-4">Simple Pricing</Badge>
            <h2 className="text-3xl font-bold mb-4">Choose Your Protection Level</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Start free. Upgrade when you need more lookups.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* Free */}
            <Card className="bg-card border-border relative">
              <CardHeader>
                <CardTitle className="text-lg">Free</CardTitle>
                <div className="mt-2">
                  <span className="text-4xl font-bold">$0</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 2 phone lookups/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 2 email checks/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Basic scam risk score</li>
                  <li className="flex items-center gap-2"><X className="w-4 h-4 text-muted-foreground" /> No social search</li>
                  <li className="flex items-center gap-2"><X className="w-4 h-4 text-muted-foreground" /> No IP tracking</li>
                </ul>
                <Button className="w-full" variant="outline" onClick={() => navigate("/register")}>
                  Start Free
                </Button>
              </CardContent>
            </Card>

            {/* Basic */}
            <Card className="bg-card border-red-500/50 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-red-500 text-white">Most Popular</Badge>
              </div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Zap className="w-5 h-5 text-red-500" />
                  Scam Fighter
                </CardTitle>
                <div className="mt-2">
                  <span className="text-4xl font-bold">${PRICING.basic.monthly}</span>
                  <span className="text-muted-foreground">/mo</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 20 phone lookups/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 20 email checks/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 20 social searches/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> VoIP & burner detection</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Detailed scam reports</li>
                </ul>
                <Button className="w-full bg-red-500 hover:bg-red-600" onClick={() => navigate("/register?plan=basic")}>
                  Get Protected
                </Button>
              </CardContent>
            </Card>

            {/* Advanced */}
            <Card className="bg-card border-border relative">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Star className="w-5 h-5 text-purple-500" />
                  Scam Hunter
                </CardTitle>
                <div className="mt-2">
                  <span className="text-4xl font-bold">${PRICING.advanced.monthly}</span>
                  <span className="text-muted-foreground">/mo</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 100 lookups each type</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> IP geolocation tracking</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 95% VPN detection</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Email header analysis</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Export reports</li>
                </ul>
                <Button className="w-full" variant="outline" onClick={() => navigate("/register?plan=advanced")}>
                  Upgrade Now
                </Button>
              </CardContent>
            </Card>

            {/* Professional */}
            <Card className="bg-card border-amber-500/30 relative">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Crown className="w-5 h-5 text-amber-500" />
                  Professional
                </CardTitle>
                <div className="mt-2">
                  <span className="text-4xl font-bold">${PRICING.professional.monthly}</span>
                  <span className="text-muted-foreground">/mo</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 500 lookups each type</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Tracking pixel & links</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> API access</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Team collaboration</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Priority support</li>
                </ul>
                <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black" onClick={() => navigate("/register?plan=professional")}>
                  Go Professional
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Pay Per Search Option */}
          <div className="mt-8 text-center">
            <p className="text-muted-foreground">
              Need just one lookup? <span className="text-foreground font-medium">Single reports available from $2.99</span>
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-10">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-red-500">1</span>
              </div>
              <h3 className="font-semibold mb-2">Enter the Info</h3>
              <p className="text-sm text-muted-foreground">Paste the suspicious phone number, email, or link you received</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-red-500">2</span>
              </div>
              <h3 className="font-semibold mb-2">We Investigate</h3>
              <p className="text-sm text-muted-foreground">Our system checks multiple databases and returns detailed results</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-red-500">3</span>
              </div>
              <h3 className="font-semibold mb-2">Stay Protected</h3>
              <p className="text-sm text-muted-foreground">Get a clear scam risk score and know exactly who you're dealing with</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-red-500">
        <div className="container mx-auto text-center max-w-2xl">
          <Shield className="w-16 h-16 mx-auto mb-6 text-white" />
          <h2 className="text-3xl font-bold mb-4 text-white">Don't Wait Until It's Too Late</h2>
          <p className="text-white/80 mb-8">
            Scammers are getting smarter. Protect yourself and your family today.
          </p>
          <Button size="lg" variant="secondary" onClick={() => navigate("/register")} className="gap-2">
            Start Your Free Account
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Shield className="w-5 h-5 text-red-500" />
            <span className="font-semibold"><span className="text-red-500">Scam</span>Finder</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} ScamFinder. Protecting people from fraud.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
